import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, MapPin, Pencil, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface Location {
  id: string;
  name: string;
  code: string | null;
  locationType: string;
  isPrimary: boolean;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  stateRegion: string | null;
  postalCode: string | null;
  countryCode: string | null;
  phone: string | null;
  email: string | null;
  receivingHours: string | null;
  deliveryNotes: string | null;
}

const LOCATION_TYPES = [
  { value: "warehouse", label: "Warehouse" },
  { value: "office", label: "Office" },
  { value: "distribution_center", label: "Distribution Center" },
  { value: "retail", label: "Retail" },
  { value: "other", label: "Other" },
];

function typeLabel(type: string) {
  return LOCATION_TYPES.find(t => t.value === type)?.label || type || "Warehouse";
}

function formatAddress(loc: Location) {
  return [loc.addressLine1, loc.city, loc.stateRegion, loc.postalCode].filter(Boolean).join(", ");
}

const emptyForm = {
  name: "", code: "", locationType: "warehouse", isPrimary: false,
  addressLine1: "", addressLine2: "", city: "", stateRegion: "",
  postalCode: "", countryCode: "US", phone: "", email: "",
  receivingHours: "", deliveryNotes: "",
};

export default function LocationsSettingsPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
    queryFn: () => api.get<Location[]>(`/locations`, sessionId),
  });

  const saveMutation = useMutation({
    mutationFn: () => editingId
      ? api.patch(`/locations/${editingId}`, form, sessionId)
      : api.post(`/locations`, form, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      setDialogOpen(false);
      setEditingId(null);
      toast({ title: editingId ? "Location updated" : "Location created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/locations/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      toast({ title: "Location deleted" });
    },
  });

  const handleEdit = (loc: Location) => {
    setEditingId(loc.id);
    setForm({
      name: loc.name, code: loc.code || "",
      locationType: loc.locationType || "warehouse",
      isPrimary: loc.isPrimary || false,
      addressLine1: loc.addressLine1 || "", addressLine2: loc.addressLine2 || "",
      city: loc.city || "", stateRegion: loc.stateRegion || "",
      postalCode: loc.postalCode || "", countryCode: loc.countryCode || "US",
      phone: loc.phone || "", email: loc.email || "",
      receivingHours: loc.receivingHours || "",
      deliveryNotes: loc.deliveryNotes || "",
    });
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleDelete = (loc: Location) => {
    if (confirm(`Delete location "${loc.name}"?`)) {
      deleteMutation.mutate(loc.id);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Company Locations"
        description="Manage your organization's warehouses, offices, and distribution centers"
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew} data-testid="button-new-location">
              <Plus className="h-4 w-4 mr-1" /> New Location
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="h-4 w-4" /> All Locations ({locations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {locations.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No locations yet.</p>
          ) : (
            <div className="space-y-2">
              {locations.map((loc) => (
                <div
                  key={loc.id}
                  className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/40"
                  data-testid={`location-row-${loc.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{loc.name}</span>
                      {loc.code && (
                        <Badge variant="outline" className="text-[10px] font-mono">{loc.code}</Badge>
                      )}
                      <Badge variant="secondary" className="text-[10px]">{typeLabel(loc.locationType)}</Badge>
                      {loc.isPrimary && (
                        <Badge className="text-[10px] gap-1">
                          <Star className="h-3 w-3 fill-current" />
                          Primary
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatAddress(loc) || "No address on file"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(loc)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(loc)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Location" : "Add Location"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Name *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Main Warehouse" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Code</label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="MW-01" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Type *</label>
              <Select value={form.locationType} onValueChange={(v) => setForm({ ...form, locationType: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isPrimary}
                onChange={(e) => setForm({ ...form, isPrimary: e.target.checked })}
              />
              Primary Location
            </label>

            <div className="space-y-3 border-t pt-3">
              <h3 className="text-sm font-medium">Address</h3>
              <div>
                <label className="text-sm font-medium mb-1 block">Address Line 1</label>
                <Input value={form.addressLine1} onChange={(e) => setForm({ ...form, addressLine1: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Address Line 2</label>
                <Input value={form.addressLine2} onChange={(e) => setForm({ ...form, addressLine2: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">City</label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">State / Region</label>
                  <Input value={form.stateRegion} onChange={(e) => setForm({ ...form, stateRegion: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Postal Code</label>
                  <Input value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Country</label>
                  <Input value={form.countryCode} onChange={(e) => setForm({ ...form, countryCode: e.target.value })} />
                </div>
              </div>
            </div>

            <div className="space-y-3 border-t pt-3">
              <h3 className="text-sm font-medium">Contact & Logistics</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Phone</label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Email</label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Receiving Hours</label>
                <Input value={form.receivingHours} onChange={(e) => setForm({ ...form, receivingHours: e.target.value })} placeholder="Mon-Fri 8am-5pm" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Delivery Notes</label>
                <Textarea value={form.deliveryNotes} onChange={(e) => setForm({ ...form, deliveryNotes: e.target.value })} />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={!form.name || saveMutation.isPending}
              >
                {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save Changes" : "Create Location"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
